import React, { useState, useEffect, useCallback } from 'react';
import { useError } from '../../contexts/ErrorContext';
import { gameService } from '../../services/gameService';
import { User } from '../../types';
import '../../styles/components/escape-prison.scss';

interface EscapePrisonProps {
  user: User;
  onEscape: () => void;
  onClose: () => void;
}

const EscapePrison: React.FC<EscapePrisonProps> = ({ user, onEscape, onClose }) => {
  const { showError, showSuccess } = useError();
  const [squares, setSquares] = useState<Array<{ id: number; hasKey: boolean; revealed: boolean; clicked: boolean }>>([]);
  const [keysFound, setKeysFound] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts] = useState(5);

  const initializeGame = useCallback(() => {
    const newSquares = Array.from({ length: 16 }, (_, index) => ({
      id: index,
      hasKey: false,
      revealed: false,
      clicked: false
    }));

    // Place 3 keys randomly
    const keyPositions: number[] = [];
    while (keyPositions.length < 3) {
      const randomPos = Math.floor(Math.random() * 16);
      if (!keyPositions.includes(randomPos)) {
        keyPositions.push(randomPos);
      }
    }

    keyPositions.forEach(pos => {
      newSquares[pos].hasKey = true;
    });

    setSquares(newSquares);
    setKeysFound(0);
    setGameComplete(false);
    setAttempts(0);
  }, []);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  const handleSquareClick = useCallback((squareId: number) => {
    if (gameComplete || attempts >= maxAttempts) return;

    const updatedSquares = squares.map(square => {
      if (square.id === squareId && !square.clicked) {
        const newSquare = { ...square, revealed: true, clicked: true };
        if (newSquare.hasKey) {
          setKeysFound(prev => prev + 1);
        }
        return newSquare;
      }
      return square;
    });

    setSquares(updatedSquares);
    setAttempts(prev => prev + 1);

    // Check if game is complete
    const newCount = updatedSquares.filter(s => s.hasKey && s.revealed).length;
    if (newCount === 3) {
      setGameComplete(true);
      showSuccess('Congratulations! You found all the keys!');
    } else if (attempts + 1 >= maxAttempts) {
      showError('Game over! You ran out of attempts.');
    }
  }, [squares, gameComplete, attempts, maxAttempts, showSuccess, showError]);

  const handleEscape = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const response = await fetch(`${apiUrl}/game/escape-jail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        showSuccess('Successfully escaped from jail!');
        onEscape();
      } else {
        showError('Failed to escape from jail');
      }
    } catch (error) {
      console.error('Error escaping jail:', error);
      showError('Failed to escape from jail');
    }
  }, [onEscape, showSuccess, showError]);

  const getSquareContent = (square: any) => {
    if (!square.revealed) return '?';
    return square.hasKey ? '🗝️' : '❌';
  };

  const getSquareClass = (square: any) => {
    let className = 'escape-square';
    if (square.revealed) {
      className += square.hasKey ? ' has-key' : ' no-key';
    }
    if (square.clicked) {
      className += ' clicked';
    }
    return className;
  };

  return (
    <div className="escape-prison">
      <div className="escape-header">
        <h2>🚔 Escape from Prison</h2>
        <p>Find all 3 keys to escape! You have {maxAttempts - attempts} attempts remaining.</p>
      </div>

      <div className="escape-stats">
        <div className="stat">
          <span className="label">Keys Found:</span>
          <span className="value">{keysFound}/3</span>
        </div>
        <div className="stat">
          <span className="label">Attempts:</span>
          <span className="value">{attempts}/{maxAttempts}</span>
        </div>
      </div>

      <div className="escape-grid">
        {squares.map(square => (
          <button
            key={square.id}
            className={getSquareClass(square)}
            onClick={() => handleSquareClick(square.id)}
            disabled={gameComplete || attempts >= maxAttempts || square.clicked}
          >
            {getSquareContent(square)}
          </button>
        ))}
      </div>

      {gameComplete && (
        <div className="escape-actions">
          <button className="btn btn-success" onClick={handleEscape}>
            🚪 Escape from Jail
          </button>
        </div>
      )}

      {(attempts >= maxAttempts && !gameComplete) && (
        <div className="escape-actions">
          <button className="btn btn-primary" onClick={initializeGame}>
            🔄 Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default EscapePrison;
